import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/api';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Input, Select, Label, Icon } from '../components/ui';
import { IconPlus } from '@tabler/icons-react';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import ExportModal from '../components/ExportModal';

const Users = () => {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'collaborator',
    phone: '',
    password: '',
    confirmPassword: '',
    active: true,
    assignedClients: []
  });

  const { hasPermission, currentUser, canAccessClient, canModifyResource, canDeleteResource } = usePermissions();

  const [filtres, setFiltres] = useState({ recherche: '', role: '' });
  const [showExportModal, setShowExportModal] = useState(false);

  const filtrerUsers = () => {
    return list.filter(user => {
      if (currentUser?.role === 'collaborator' && user.role !== 'client') return false;
      const r = filtres.recherche.toLowerCase();
      if (r) {
        const clientNames = getClientNames(user).join(' ').toLowerCase();
        const matchNom = (user.name || '').toLowerCase().includes(r);
        const matchEmail = (user.email || '').toLowerCase().includes(r);
        const matchTel = (user.phone || '').toLowerCase().includes(r);
        const matchClient = clientNames.includes(r);
        if (!matchNom && !matchEmail && !matchTel && !matchClient) return false;
      }
      if (filtres.role && user.role !== filtres.role) return false;
      return true;
    });
  };

  const load = useCallback(async () => {
    setGlobalLoading(true);
    try {
      if (currentUser?.role === 'admin') {
        const [usersResponse, clientsResponse] = await Promise.all([
          api.get('/users'),
          api.get('/clients')
        ]);
        setList(usersResponse.data || []);
        setClients(clientsResponse.data || []);
      } else if (currentUser?.role === 'collaborator') {
        const assignedClientIds = currentUser.assignedClients?.map(c => c._id || c) || [];
        if (assignedClientIds.length > 0) {
          try {
            const clientsResponse = await api.get('/clients', {
              params: { ids: assignedClientIds.join(',') }
            });
            const usersResponse = await api.get('/users', {
              params: { role: 'client', clientIds: assignedClientIds.join(',') }
            });
            setList(usersResponse.data || []);
            setClients(clientsResponse.data || []);
          } catch (apiError) {
            console.error('Erreur API chargement:', apiError);
            const [allUsersResponse, allClientsResponse] = await Promise.all([
              api.get('/users'),
              api.get('/clients')
            ]);
            const allUsers = allUsersResponse.data || [];
            const allClients = allClientsResponse.data || [];
            const filteredClients = allClients.filter(client => assignedClientIds.includes(client._id));
            const filteredUsers = allUsers.filter(user => user.role === 'client' && filteredClients.some(client => client.email === user.email));
            setList(filteredUsers);
            setClients(filteredClients);
          }
        } else {
          setList([]);
          setClients([]);
        }
      } else if (currentUser?.role === 'client') {
        setList([]);
        setClients([]);
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
      alert('Erreur lors du chargement des données');
    } finally {
      setGlobalLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const normalizeAssignedClients = (user) => {
    if (!user.assignedClients) return [];
    if (user.assignedClients.length > 0 && typeof user.assignedClients[0] === 'object') {
      return user.assignedClients.map(client => client._id);
    }
    return user.assignedClients;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'collaborator',
      phone: '',
      password: '',
      confirmPassword: '',
      active: true,
      assignedClients: []
    });
    setEditingUser(null);
  };

  const handleCreateUser = () => {
    if (!hasPermission('users', 'create')) {
      alert('❌ Vous n\'avez pas la permission de créer des utilisateurs');
      return;
    }
    resetForm();
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    if (!canModifyResource(user, 'users')) {
      alert('❌ Vous n\'avez pas la permission de modifier cet utilisateur');
      return;
    }
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'collaborator',
      phone: user.phone || '',
      password: '',
      confirmPassword: '',
      active: user.active !== false,
      assignedClients: normalizeAssignedClients(user)
    });
    setEditingUser(user);
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleClientSelection = (clientId) => {
    setFormData(prev => {
      const newAssignedClients = prev.assignedClients.includes(clientId)
        ? prev.assignedClients.filter(id => id !== clientId)
        : [...prev.assignedClients, clientId];
      return { ...prev, assignedClients: newAssignedClients };
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) { alert('Le nom est requis'); return false; }
    if (!formData.email.trim()) { alert('L\'email est requis'); return false; }
    if (!formData.email.includes('@')) { alert('Email invalide'); return false; }
    if (!editingUser && !formData.password) { alert('Le mot de passe est requis pour la création'); return false; }
    if (formData.password && formData.password.length < 6) { alert('Le mot de passe doit contenir au moins 6 caractères'); return false; }
    if (formData.password !== formData.confirmPassword) { alert('Les mots de passe ne correspondent pas'); return false; }
    if (formData.role === 'collaborator' && formData.assignedClients.length === 0) { alert('Veuillez assigner au moins un client au collaborateur'); return false; }
    return true;
  };

  const saveUser = async () => {
    if (!validateForm()) return;
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        phone: formData.phone,
        active: formData.active
      };
      if (formData.password) userData.password = formData.password;
      if (formData.role === 'collaborator') userData.assignedClients = formData.assignedClients;
      if (editingUser) {
        await api.put(`/users/${editingUser._id}`, userData);
        alert('✅ Utilisateur mis à jour avec succès');
      } else {
        await api.post('/users', userData);
        alert('✅ Utilisateur créé avec succès');
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const remove = async (user) => {
    if (!hasPermission('users', 'delete')) {
      alert('❌ Vous n\'avez pas la permission de supprimer des utilisateurs');
      return;
    }
    if (user._id === currentUser?._id) {
      alert('❌ Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    if (!canDeleteResource(user, 'users')) {
      alert('❌ Vous ne pouvez pas supprimer cet utilisateur');
      return;
    }
    if (!await showConfirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.name} ?`)) return;
    try {
      await api.delete('/users/' + user._id);
      load();
      alert('✅ Utilisateur supprimé avec succès');
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const canModifyUser = (user) => {
    if (user._id === currentUser?._id) return false;
    return canModifyResource(user, 'users');
  };

  const canDeleteUser = (user) => {
    if (user._id === currentUser?._id) return false;
    return canDeleteResource(user, 'users');
  };

  const handleCreateClientUser = () => {
    window.location.href = '/clients/new';
  };

  const getClientNames = (user) => {
    const assignedClients = normalizeAssignedClients(user);
    return assignedClients.map(clientId => {
      const client = clients.find(c => c._id === clientId);
      return client?.companyName || 'Client inconnu';
    });
  };

  const userColumns = [
    { key: 'name', label: 'Nom', accessor: (u) => u?.name || '' },
    { key: 'email', label: 'Email', accessor: (u) => u?.email || '' },
    { key: 'role', label: 'Rôle', accessor: (u) => u?.role || '' },
    { key: 'phone', label: 'Téléphone', accessor: (u) => u?.phone || '' },
    { key: 'clients', label: 'Clients Assignés', accessor: (u) => getClientNames(u).join(', ') },
    { key: 'status', label: 'Statut', accessor: (u) => u?.active !== false ? 'Actif' : 'Inactif' },
  ];

  if (globalLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement des utilisateurs...</div>
      </div>
    );
  }

  if (currentUser?.role === 'client') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold text-red-800 mb-2">Accès non autorisé</h2>
          <p className="text-gray-600">
            Vous n&apos;avez pas les permissions nécessaires pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Gestion des Utilisateurs</h2>
        <div className="flex space-x-4">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} disabled={filtrerUsers().length === 0}>
            <Icon name="IconFileExport" size={16} className="mr-1" /> Export
          </Button>
          {currentUser?.role === 'admin' && hasPermission('users', 'create') && (
            <Button variant="primary" onClick={handleCreateUser}>
              <Icon name="IconPlus" size={16} className="mr-1" /> Nouvel utilisateur
            </Button>
          )}
          {currentUser?.role === 'collaborator' && hasPermission('clients', 'create') && (
            <Button variant="success" onClick={handleCreateClientUser}>
              <Icon name="IconPlus" size={16} className="mr-1" /> Nouveau client
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Recherche (nom, email, tél, client assigné)</Label>
            <Input
              type="text"
              value={filtres.recherche}
              onChange={e => setFiltres(prev => ({ ...prev, recherche: e.target.value }))}
              placeholder="Rechercher..."
            />
          </div>
          <div>
            <Label>Rôle</Label>
            <Select
              value={filtres.role}
              onChange={e => setFiltres(prev => ({ ...prev, role: e.target.value }))}
            >
              <option value="">Tous les rôles</option>
              <option value="admin">Administrateur</option>
              <option value="collaborator">Collaborateur</option>
              <option value="client">Client</option>
            </Select>
          </div>
        </div>
        <div className="flex justify-between items-center mt-3">
          <span className="text-xs text-gray-500">{filtrerUsers().length} utilisateur(s) trouvé(s)</span>
          <Button variant="secondary" size="sm" onClick={() => setFiltres({ recherche: '', role: '' })}>
            Réinitialiser
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clients Assignés</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtrerUsers().map(user => {
                const clientNames = getClientNames(user);
                return (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'collaborator' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.role === 'admin' ? 'Administrateur' :
                         user.role === 'collaborator' ? 'Collaborateur' : 'Client'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.phone || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs">
                        {clientNames.slice(0, 2).map((name, index) => (
                          <span key={index} className="inline-block bg-gray-100 rounded px-2 py-1 text-xs mr-1 mb-1">{name}</span>
                        ))}
                        {clientNames.length > 2 && (
                          <span className="text-xs text-gray-400">+{clientNames.length - 2} autres</span>
                        )}
                        {clientNames.length === 0 && (
                          <span className="text-xs text-gray-400">Aucun</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.active ? 'Actif' : 'Inactif'}
                      </span>
                      {user.role === 'client' && user.firstLogin && (
                        <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Première connexion</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-4">
                        {canModifyUser(user) && (
                          <Button variant="ghost" size="sm" onClick={() => handleEditUser(user)}>Modifier</Button>
                        )}
                        {canDeleteUser(user) && (
                          <Button variant="danger" size="sm" onClick={() => remove(user)}>Supprimer</Button>
                        )}
                        {user.role === 'client' && currentUser?.role === 'collaborator' && canAccessClient(user._id) && (
                          <Button variant="ghost" size="sm" onClick={() => window.location.href = `/invoices?clientId=${user._id}`}>Factures</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {list.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {currentUser?.role === 'collaborator' ? "Aucun client assigné à votre compte." : "Aucun utilisateur trouvé."}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-6">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h3>

              <div className="space-y-4">
                <div>
                  <Label required>Nom</Label>
                  <Input type="text" name="name" value={formData.name} onChange={handleFormChange} required />
                </div>

                <div>
                  <Label required>Email</Label>
                  <Input type="email" name="email" value={formData.email} onChange={handleFormChange} required />
                </div>

                <div>
                  <Label>Téléphone</Label>
                  <Input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} placeholder="+212 6XX XXX XXX" />
                </div>

                <div>
                  <Label required>Rôle</Label>
                  <Select name="role" value={formData.role} onChange={handleFormChange}>
                    <option value="collaborator">Collaborateur</option>
                    <option value="admin">Administrateur</option>
                  </Select>
                </div>

                {(!editingUser || formData.password) && (
                  <>
                    <div>
                      <Label required={!editingUser}>{editingUser ? 'Nouveau mot de passe' : 'Mot de passe'}</Label>
                      <Input type="password" name="password" value={formData.password} onChange={handleFormChange} placeholder={editingUser ? 'Laissez vide pour garder l\'actuel' : ''} required={!editingUser} />
                    </div>
                    <div>
                      <Label>Confirmer le mot de passe</Label>
                      <Input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleFormChange} />
                    </div>
                  </>
                )}

                {formData.role === 'collaborator' && clients.length > 0 && (
                  <div>
                    <Label>Clients assignés * (sélection multiple)</Label>
                    <div className="border rounded p-3 max-h-60 overflow-y-auto">
                      {clients.map(client => (
                        <div key={client._id} className="flex items-center mb-2">
                          <input type="checkbox" id={`client-${client._id}`} checked={formData.assignedClients.includes(client._id)} onChange={() => handleClientSelection(client._id)} className="mr-2" />
                          <label htmlFor={`client-${client._id}`} className="text-sm">{client.companyName} - {client.email}</label>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{formData.assignedClients.length} client(s) sélectionné(s)</div>
                  </div>
                )}

                <div className="flex items-center">
                  <input type="checkbox" name="active" id="active" checked={formData.active} onChange={handleFormChange} className="mr-2" />
                  <label htmlFor="active" className="text-sm font-medium">Compte actif</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="secondary" type="button" onClick={() => { setShowForm(false); resetForm(); }}>Annuler</Button>
                <Button variant="primary" type="button" onClick={saveUser}>{editingUser ? 'Mettre à jour' : 'Créer'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          columns={userColumns}
          data={filtrerUsers()}
          title="Liste des utilisateurs"
          filename="utilisateurs"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

export default Users;